/*
  TemplateRegistry.ts
  Minimal in-app template registry for MVP. Production system should read from backend Template Registry.
*/

export type DocumentTypeKey =
  | 'motion'
  | 'demurrer'
  | 'strike'
  | 'rjn'
  | 'ex_parte'
  | 'proposed_order'
  | 'declaration'
  | 'letter'
  | 'contract'
  | 'other';

export interface TemplateMetadata {
  id: string;
  name: string;
  documentType: DocumentTypeKey;
  format: 'pleading' | 'non_pleading' | 'form';
  jurisdiction: string; // e.g. 'US/CA'
  version: string;
  placeholders: string[];
  lexicalJson: unknown; // Lexical JSON skeleton with placeholders like {{PLACEHOLDER}}
}

// CA Motion (Pleading) baseline skeleton
const CA_MOTION_PLEADING: TemplateMetadata = {
  id: 'CA_MOTION_PLEADING_BASELINE_V1',
  name: 'CA Motion (Pleading)',
  documentType: 'motion',
  format: 'pleading',
  jurisdiction: 'US/CA',
  version: '1.0.0',
  placeholders: [
    'COURT_NAME',
    'COUNTY_NAME',
    'CASE_NUMBER',
    'PLAINTIFF',
    'DEFENDANT',
    'DEPT',
    'JUDGE',
    'TITLE_OF_DOCUMENT',
    'HEARING_DATE',
    'HEARING_TIME',
    'HEARING_LOCATION',
    'ATTORNEY_BLOCK',
    'INTRO',
    'FACTS',
    'ARGUMENT',
    'PRAYER',
    'SIGNATURE'
  ],
  // Minimal Lexical JSON document with placeholder paragraphs.
  // Editors/plugins (e.g., PleadingCaptionNode, LineNumberingPlugin) will progressively enrich this.
  lexicalJson: {
    root: {
      type: 'root',
      children: [
        {
          type: 'paragraph',
          children: [
            { type: 'text', text: '{{ATTORNEY_BLOCK}}', detail: 0, format: 0, mode: 'normal', style: '' }
          ],
          direction: 'ltr',
          format: '',
          indent: 0,
          version: 1
        },
        {
          type: 'paragraph',
          children: [
            { type: 'text', text: '{{COURT_NAME}}, {{COUNTY_NAME}}', detail: 0, format: 0, mode: 'normal', style: '' }
          ],
          direction: 'ltr',
          format: '',
          indent: 0,
          version: 1
        },
        {
          type: 'paragraph',
          children: [
            { type: 'text', text: '{{PLAINTIFF}} v. {{DEFENDANT}}', detail: 0, format: 0, mode: 'normal', style: '' }
          ],
          direction: 'ltr',
          format: '',
          indent: 0,
          version: 1
        },
        {
          type: 'paragraph',
          children: [
            { type: 'text', text: 'Case No.: {{CASE_NUMBER}} | Dept: {{DEPT}} | Judge: {{JUDGE}}', detail: 0, format: 0, mode: 'normal', style: '' }
          ],
          direction: 'ltr',
          format: '',
          indent: 0,
          version: 1
        },
        {
          type: 'paragraph',
          children: [
            { type: 'text', text: '{{TITLE_OF_DOCUMENT}}', detail: 0, format: 1, mode: 'normal', style: '' }
          ],
          direction: 'ltr',
          format: '',
          indent: 0,
          version: 1
        },
        {
          type: 'paragraph',
          children: [
            { type: 'text', text: 'Hearing: {{HEARING_DATE}} at {{HEARING_TIME}} – {{HEARING_LOCATION}}', detail: 0, format: 0, mode: 'normal', style: '' }
          ],
          direction: 'ltr',
          format: '',
          indent: 0,
          version: 1
        },
        { type: 'paragraph', children: [{ type: 'text', text: '', detail: 0, format: 0, mode: 'normal', style: '' }], direction: 'ltr', format: '', indent: 0, version: 1 },
        {
          type: 'paragraph',
          children: [{ type: 'text', text: '{{INTRO}}', detail: 0, format: 0, mode: 'normal', style: '' }],
          direction: 'ltr',
          format: '',
          indent: 0,
          version: 1
        },
        {
          type: 'paragraph',
          children: [{ type: 'text', text: '{{FACTS}}', detail: 0, format: 0, mode: 'normal', style: '' }],
          direction: 'ltr',
          format: '',
          indent: 0,
          version: 1
        },
        {
          type: 'paragraph',
          children: [{ type: 'text', text: '{{ARGUMENT}}', detail: 0, format: 0, mode: 'normal', style: '' }],
          direction: 'ltr',
          format: '',
          indent: 0,
          version: 1
        },
        {
          type: 'paragraph',
          children: [{ type: 'text', text: '{{PRAYER}}', detail: 0, format: 0, mode: 'normal', style: '' }],
          direction: 'ltr',
          format: '',
          indent: 0,
          version: 1
        },
        {
          type: 'paragraph',
          children: [{ type: 'text', text: '{{SIGNATURE}}', detail: 0, format: 0, mode: 'normal', style: '' }],
          direction: 'ltr',
          format: '',
          indent: 0,
          version: 1
        }
      ],
      direction: 'ltr',
      format: '',
      indent: 0,
      version: 1
    }
  }
};

const REGISTRY: TemplateMetadata[] = [CA_MOTION_PLEADING];

export function listTemplates(): TemplateMetadata[] {
  return REGISTRY.slice();
}

export function getTemplateById(id?: string): TemplateMetadata | undefined {
  if (!id) return undefined;
  return REGISTRY.find(t => t.id === id);
}

export function selectTemplateByClassification(params: {
  documentType?: DocumentTypeKey;
  jurisdiction?: string;
  format?: 'pleading' | 'non_pleading' | 'form';
}): TemplateMetadata | undefined {
  const { documentType, jurisdiction, format } = params;
  return REGISTRY.find(
    t => (!documentType || t.documentType === documentType) && (!jurisdiction || t.jurisdiction === jurisdiction) && (!format || t.format === format)
  );
}


